import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { selectUserProjectIsUuidBased } from 'core/userContext';
import { AceEditor } from 'modules/aceEditor';
import { Button, BUTTON_SIZES, BUTTON_VARIANTS } from 'ui/button';
import { useDrawerContext } from 'ui/fullScreen';
import { WarningFill } from 'ui/icons';
import { InputText } from 'ui/inputs';
import { BottomTooltip } from 'ui/tooltip';
import noop, { NoOp } from 'utils/noop';
import { isValidEmail } from 'utils/stringHelpers';

import {
  AceEditorError,
  AceEditorWrapper,
  AceEditorWrapperContainer,
  Container,
  DataBar,
  NoDataFeeds,
  SectionHeader,
} from '.';
import { useEditableFieldsContext } from '../context/EditableFieldsContext';
import { usePreviewWithDataContext } from '../context/PreviewWithDataContext';
import { displayContent } from './content';
import { UserProfileDataType } from './types';
import { useGetHasUserProfilePermission } from './utils';

export const StyledBottomTooltipForDataBar = styled(BottomTooltip)`
  flex: 0 0 auto;
`;

export const CONTAINER_ID = 'template-editor-preview-data';
export const FORM_ID = 'preview-with-data-pane';
export const INPUT_TEXT_USER_ID = 'preview-input-user-id';
export const LOAD_BUTTON_ID = 'preview-btn-load-user';
export const CLEAR_BUTTON_ID = 'preview-btn-clear-user';
export const ACE_EDITOR_ERROR_ID = 'ace-editor-error-id';
export const DATA_FEEDS_COUNT = 'data-feed-count';
export const PREVIEW_BUTTON_LOAD_DATA = 'preview-btn-load-data';
export const PREVIEW_BUTTON_CLEAR_DATA = 'preview-btn-clear-data';
export const NO_DATA_FEEDS = 'no-data-feeds';

type Props = {
  onRefreshPreview: NoOp;
  showNullForDataFeedEmptyState?: boolean;
  showUserData?: boolean;
};

const NoDataFeedsContainer = () => {
  const { toggleDrawer } = useDrawerContext();
  return (
    <NoDataFeeds data-test={NO_DATA_FEEDS}>
      {toggleDrawer === noop ? (
        <span>{displayContent.strNoAvailableDataFeeds}</span>
      ) : (
        <>
          <span>{displayContent.strNoDataFeeds}</span>
          <a onClick={() => toggleDrawer()}>+ Add data feed</a>
        </>
      )}
    </NoDataFeeds>
  );
};

export const DataPane = ({
  onRefreshPreview,
  showNullForDataFeedEmptyState = false,
  showUserData = true,
}: Props) => {
  const { liveTemplate } = useEditableFieldsContext();
  const {
    dataFeedData,
    dataFeedDataIsValid,
    isLoadingDataFeeds,
    onLoadDataFeeds,
    onLoadUserData,
    setDataFeedData,
    setUserData,
    setIsUserDataDirty,
    userData,
    userDataIsValid,
  } = usePreviewWithDataContext();
  const [userId, setUserId] = useState<string>('');
  const isUuidBased = useSelector(selectUserProjectIsUuidBased);
  const canLoadUserData = isUuidBased
    ? !!userId.trim() // TBD
    : isValidEmail(userId.trim());
  const userDataType =
    isUuidBased && !isValidEmail(userId.trim())
      ? UserProfileDataType.UserId
      : UserProfileDataType.Email;
  const dataFeedCount = liveTemplate?.dataFeedIds?.length;
  const isLoadUserDataDisabled = !useGetHasUserProfilePermission();

  useEffect(() => {
    // manually refresh the preview when the component loads
    onRefreshPreview();
  }, []);

  return (
    <Container
      className={showNullForDataFeedEmptyState ? 'hasNullEmptyDataFeed' : ''}
      data-test={CONTAINER_ID}
    >
      {showUserData && (
        <>
          <SectionHeader>{displayContent.headerUserData}</SectionHeader>
          <DataBar>
            <form
              // method controls what happens when the form is submitted.
              // setting it to dialog is a hack to make sure the autocomplete on the input still works,
              // because the autocomplete will only be populated when the form action completes.
              // see https://iterable.atlassian.net/browse/TNT-5428 for more context
              method="dialog"
              id={FORM_ID}
              onSubmit={() => {
                onLoadUserData(userId.trim(), userDataType);
              }}
            >
              <InputText
                data-test={INPUT_TEXT_USER_ID}
                id={INPUT_TEXT_USER_ID}
                onChange={setUserId}
                autoComplete={'on'}
                placeholder={
                  isUuidBased
                    ? displayContent.placeholderUserEmailOrId
                    : displayContent.placeholderUserEmail
                }
                value={userId}
                width="100%"
              />
            </form>

            <StyledBottomTooltipForDataBar
              isEnabled={isLoadUserDataDisabled}
              tooltipContent={displayContent.tooltipUserProfilePermission}
            >
              <Button
                type="submit"
                form="preview-with-data-pane"
                data-test={LOAD_BUTTON_ID}
                isDisabled={!canLoadUserData || isLoadUserDataDisabled}
                size={BUTTON_SIZES.LARGE}
                variant={BUTTON_VARIANTS.SECONDARY}
              >
                <span>{displayContent.btnLoadUserData}</span>
              </Button>
            </StyledBottomTooltipForDataBar>
            <Button
              data-test={CLEAR_BUTTON_ID}
              onClick={() => {
                setUserData('{}');
                setIsUserDataDirty(true);
              }}
              size={BUTTON_SIZES.LARGE}
              variant={BUTTON_VARIANTS.GHOST}
            >
              <span>{displayContent.btnClearUserData}</span>
            </Button>
          </DataBar>
          <AceEditorWrapperContainer>
            <AceEditorWrapper hasError={!userDataIsValid}>
              <AceEditor
                editorProps={{
                  $blockScrolling: Infinity,
                }}
                height={'230px'} // Ace Editor has an internal min-height: 300px
                mode="json"
                name="template-edit-user-data"
                onChange={(value) => {
                  setUserData(value);
                  setIsUserDataDirty(true);
                }}
                value={userData}
                wrapEnabled={true}
              />
            </AceEditorWrapper>
            {!userDataIsValid && (
              <AceEditorError data-test={ACE_EDITOR_ERROR_ID}>
                <WarningFill isColorInherited />
                <span>{displayContent.errSyntax}</span>
              </AceEditorError>
            )}
          </AceEditorWrapperContainer>
        </>
      )}
      {(dataFeedCount || !showNullForDataFeedEmptyState) && (
        <SectionHeader>{displayContent.headerDataFeeds}</SectionHeader>
      )}
      {dataFeedCount ? (
        <>
          <DataBar>
            <div data-test={DATA_FEEDS_COUNT}>
              {displayContent.strDataFeedsAdded({ feedCount: dataFeedCount })}
            </div>
            <Button
              data-test={PREVIEW_BUTTON_LOAD_DATA}
              isDisabled={!userDataIsValid}
              isLoading={isLoadingDataFeeds}
              onClick={onLoadDataFeeds}
              size={BUTTON_SIZES.LARGE}
              variant={BUTTON_VARIANTS.SECONDARY}
            >
              <span>{displayContent.btnLoadDataFeeds}</span>
            </Button>
            <Button
              data-test={PREVIEW_BUTTON_CLEAR_DATA}
              onClick={() => setDataFeedData('{}')}
              size={BUTTON_SIZES.LARGE}
              variant={BUTTON_VARIANTS.GHOST}
            >
              <span>{displayContent.btnClearDataFeeds}</span>
            </Button>
          </DataBar>
          <AceEditorWrapperContainer>
            <AceEditorWrapper hasError={!dataFeedDataIsValid}>
              <AceEditor
                editorProps={{
                  $blockScrolling: Infinity,
                }}
                height={'230px'} // Ace Editor has an internal min-height: 300px
                mode="json"
                name="template-edit-data-feed-data"
                onChange={setDataFeedData}
                value={dataFeedData}
                wrapEnabled={true}
              />
            </AceEditorWrapper>
            {!dataFeedDataIsValid && (
              <AceEditorError>
                <WarningFill isColorInherited />
                <span>{displayContent.errSyntax}</span>
              </AceEditorError>
            )}
          </AceEditorWrapperContainer>
        </>
      ) : showNullForDataFeedEmptyState ? null : (
        <NoDataFeedsContainer />
      )}
    </Container>
  );
};

export default DataPane;
